import { ChevronLeft, ChevronRight } from "lucide-react";
import { CompanyLogo } from "@/components/CompanyLogo";
import { SourceChips } from "@/components/SourceChips";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import type { BizminerRelatedInitiative } from "@/types/serializers";

interface RelatedInitiativeSheetProps {
  ri: BizminerRelatedInitiative | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

export function RelatedInitiativeSheet({
  ri,
  open,
  onOpenChange,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: RelatedInitiativeSheetProps) {
  if (!ri) return null;

  const impactItems = ri.quantitative_impact ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader className="space-y-4">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon-sm" onClick={onPrev} disabled={!hasPrev} aria-label="Go to previous related initiative">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={onNext} disabled={!hasNext} aria-label="Go to next related initiative">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <CompanyLogo
              logoUrl={ri.account.logo_url}
              name={ri.account.name}
              className="size-10 rounded-lg flex-shrink-0"
            />
            <div>
              <SheetTitle className="text-base">{ri.account.name}</SheetTitle>
              <SheetDescription className="sr-only">
                Details for {ri.name}
              </SheetDescription>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary">{ri.pillar_title}</Badge>
            <Badge variant="outline">{ri.beneficiary}</Badge>
            {ri.evidence_type && (
              <Badge variant="outline">{ri.evidence_type}</Badge>
            )}
          </div>
        </SheetHeader>

        <div className="space-y-5 px-4 pb-6 pt-2">
          <h3 className="text-lg font-semibold">{ri.name}</h3>

          {ri.business_impact_type.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Impact Category
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {ri.business_impact_type.map((type) => (
                  <Badge key={type} variant="secondary" className="text-xs">
                    {type}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {ri.short_summary && (
            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm leading-relaxed">{ri.short_summary}</p>
            </div>
          )}

          {impactItems.length > 0 && (
            <div className="rounded-lg bg-muted p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                Quantitative Impact
              </h4>
              <ul className="space-y-1">
                {impactItems.map((item) => (
                  <li key={item} className="text-sm flex items-start gap-2">
                    <span className="text-primary mt-1.5 size-1.5 rounded-full bg-primary shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <SourceChips sources={ri.sources} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
